import { useEffect, useState } from 'react';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

interface UseRecipeEditorsOptions {
  initialIngredients: string;
  initialSteps: string;
}

const EDITOR_ATTRIBUTES = {
  class: 'form-control',
  style: 'min-height:150px;',
};

const buildEditorOptions = (
  content: string,
  onUpdate: (content: string) => void
) => ({
  extensions: [StarterKit],
  content,
  editorProps: { attributes: EDITOR_ATTRIBUTES },
  onUpdate: ({ editor }: { editor: { getHTML: () => string } }) => onUpdate(editor.getHTML()),
});

const syncEditorContent = (
  editor: { commands?: { setContent: (content: string) => void } } | null | undefined,
  content: string
) => {
  editor?.commands?.setContent(content);
};

const destroyEditor = (
  editor: { destroy?: () => void } | null | undefined
) => {
  editor?.destroy?.();
};

const useRecipeEditors = ({
  initialIngredients,
  initialSteps,
}: UseRecipeEditorsOptions) => {
  const [ingredients, setIngredients] = useState(initialIngredients);
  const [steps, setSteps] = useState(initialSteps);

  const ingredientsEditor = useEditor(buildEditorOptions(ingredients, setIngredients));

  const stepsEditor = useEditor(buildEditorOptions(steps, setSteps));

  useEffect(() => {
    setIngredients(initialIngredients);
    setSteps(initialSteps);
    syncEditorContent(ingredientsEditor, initialIngredients);
    syncEditorContent(stepsEditor, initialSteps);
  }, [initialIngredients, initialSteps, ingredientsEditor, stepsEditor]);

  useEffect(() => () => {
    destroyEditor(ingredientsEditor);
    destroyEditor(stepsEditor);
  }, [ingredientsEditor, stepsEditor]);

  return {
    ingredients,
    setIngredients,
    steps,
    setSteps,
    ingredientsEditor,
    stepsEditor,
  };
};

export default useRecipeEditors;
